import React, { useEffect } from 'react';
import { TravelMapComponentFragment } from '@graphql/generated';
import { AdminAccommodationsTelemetryProvider } from './Accommodations.telemetry';
import { getAccommodationsNavigations } from './Accommodations.navigations';
import { ACCOMMODATIONS_ACTIONS, ACCOMMODATIONS_ANIMATION_CONFIG, ACCOMMODATIONS_SCREENS } from './Accommodations.constants';
import { AccommodationsFab } from './Accommodations.fab';
import { EditorPageConfig } from '@website-editor/WebsiteEditor.types';
import { Flex } from '@withjoy/joykit';
import { AccommodationsProvider } from './context/Accommodations.provider';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useQueryParamHelper } from '@shared/core/queryString';
import { CUSTOM_PROPERTY_ADD_KEY } from './Accommodations.constants';
import { GuestHotelBookingsList } from './components/GuestHotelBookingsList';
import { CustomComponentsProps } from '@website-editor/customComponents/CustomComponents';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useTranslation } from '@shared/core';
import { AccommodationsTourProvider } from './components/AccommodationsTour';
import { EventVenue } from './components/EventVenue/EventVenue';
import { VenueContainer } from './components/VenueContainer';
import { TabMenu } from './components/TabMenu';

type AccommodationsRootProps = Readonly<{
  pageSlug: string;
  eventHandle: string;
  isGuestBookingsTrackerPage?: boolean;
}>;

const AccommodationsRoot = React.memo<AccommodationsRootProps>(({ pageSlug, eventHandle, isGuestBookingsTrackerPage }) => {
  /**
   * Preload tour translations to avoid flickering on start of the tour.
   * If use Suspense, then it cause tour tooltip placement to be incorrect.
   * @todo - Cleanup once get resolved core issue - https://joylife.slack.com/archives/CNCC1QS0K/p1765395785681989
   */
  useTranslation('tour');

  return (
    <AccommodationsTourProvider>
      <Flex
        flexDirection="column"
        height="100%"
      >
        <VenueContainer
          pageSlug={pageSlug}
          eventHandle={eventHandle}
          isGuestBookingsTrackerPage={isGuestBookingsTrackerPage}
        />
        {isGuestBookingsTrackerPage ? <GuestHotelBookingsList /> : <TabMenu />}
      </Flex>
      {!isGuestBookingsTrackerPage && <AccommodationsFab />}
      <EventVenue pageSlug={pageSlug} />
      {/* Temporarily disable in prod; may re-enable for future guest map changes */}
      {/* <ExpandedMapAnnouncementDialog /> */}
    </AccommodationsTourProvider>
  );
});

AccommodationsRoot.displayName = 'AccommodationsRoot';

interface AccommodationsComponentProps
  extends Readonly<CustomComponentsProps>,
    Readonly<{
      componentData: TravelMapComponentFragment;
      pageConfig: EditorPageConfig;
    }> {}

const Accommodations: React.FC<AccommodationsComponentProps> = ({ componentData, ...props }) => {
  /**
   * Add travelMap left panel navigation animation configs
   */
  const { screens, addNavigations, navigateToScreen } = useNavigationPaneContext();
  const queryParamHelper = useQueryParamHelper();
  const isAddCustomPropertyOpen = queryParamHelper.getValueString(CUSTOM_PROPERTY_ADD_KEY);

  // Keep latest block ordering at center place to maintain order while add/remove RB/Hotel
  const { setBlockOrderData } = useCustomComponentStateContext();
  useEffect(() => {
    if (componentData) {
      setBlockOrderData({ id: componentData.id, sortOrder: componentData.sortOrder, blockOrdering: componentData.blockOrdering ?? [] });
    }
  }, [componentData, setBlockOrderData]);

  const accommodationsNavigations = getAccommodationsNavigations({ ...props, componentId: componentData.id });

  // Configure accommodations left panel navigation
  useEffect(() => {
    if (!screens.includes(ACCOMMODATIONS_SCREENS[0])) {
      addNavigations(ACCOMMODATIONS_SCREENS, ACCOMMODATIONS_ANIMATION_CONFIG, accommodationsNavigations);
    }
  }, [addNavigations, screens, accommodationsNavigations]);

  // Handle navigation state for CustomPropertyAdd
  useEffect(() => {
    if (isAddCustomPropertyOpen === 'true') {
      navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_ADD);
    }
  }, [navigateToScreen, isAddCustomPropertyOpen]);

  return (
    <AdminAccommodationsTelemetryProvider>
      <AccommodationsProvider
        componentData={componentData}
        pageConfig={props.pageConfig}
        containerId={props.containerId}
        pageSlug={props.pageSlug}
        eventHandle={props.eventHandle}
      >
        <AccommodationsRoot
          pageSlug={props.pageSlug}
          eventHandle={props.eventHandle}
          isGuestBookingsTrackerPage={props.pageConfig?.isGuestBookingsTrackerPage}
        />
      </AccommodationsProvider>
    </AdminAccommodationsTelemetryProvider>
  );
};

export { Accommodations, getAccommodationsNavigations };
